// =============================================================================
// packhouseErrorDomains — operator sentences for the reject + stock RPC family.
//
// These are `domain` registries for friendlyError() (see friendlyError.ts §5.6):
// an ordered [pattern, sentence] list that wins over its PG-code mapping.
// friendlyError owns what is true platform-wide (a duplicate key, an RLS
// denial); these rows own what is true of THESE RPCs.
//
// Why they need saying at all — two distinct failure modes:
//
//   * Most of these RAISEs land as P0001, which friendlyError passes through
//     VERBATIM by design (an RPC RAISE is usually already written for the
//     operator). These ones are not: they carry the function name as a prefix
//     and interpolate a raw UUID — an operator removing a bin from a truck read
//     "attach_bins_to_consignment: bin 4c1e-… is already on another
//     consignment".
//   * The stock-count guards raise 22023, which is in no code branch at all, so
//     they fell through to the raw text with a bare code prefix:
//     "[22023] count session is POSTED — only OPEN or SUBMITTED sessions can be
//     posted".
//
// They live together rather than at each screen because the vocabulary is
// genuinely shared: the READY/AT_STALL bin guard fires from RejectBins
// (create_reject_dispatch, transfer_reject_bins) AND RejectDispatch
// (attach_bins_to_consignment), and "stock item not found" from both Stock and
// StockCounts. Each list still stays scoped to one flow.
//
// Only the states an operator can genuinely race into are listed — the rest of
// each RPC's RAISEs are guarded client-side before the call. Anything unmatched
// still falls through to friendlyError's platform sentences, so nothing is
// swallowed; a missing row costs polish, never the message.
//
// Every pattern here is pinned against the literal RAISE text copied from the
// migration in packhouseErrorDomains.test.ts — a regex that stops matching is
// invisible on screen, so the test is the only thing that catches it.
// =============================================================================

import { friendlyError } from '@/lib/friendlyError'

// The reject-bin RPCs' own RAISE backstops (migs 297 / 479 / 20260709120000).
// Their text is accurate but was written for us, not for the floor: most carry
// the function name as a prefix and interpolate a raw bin/dispatch UUID
// ("transfer_reject_bins: bin 8f3a-… status is OPEN (must be READY or
// AT_STALL)"). friendlyError passes a P0001 through verbatim by design, so
// without these rows the operator reads our function names. Only the states an
// operator can actually race into are listed — the rest of the RAISEs are
// guarded client-side above, and anything unmatched still falls through to the
// platform sentences rather than being swallowed.
export const REJECT_BIN_DOMAIN: [RegExp, string][] = [
  // mig 20261053800000 (F06 D4) — reject_log_guard_open_bin. An entry can only
  // go into an OPEN bin; Log Rejects meets this when another tablet closed,
  // sold or released the bin after the form loaded its list. Raised as 23514,
  // which friendlyError would otherwise turn into the generic "validation
  // rules" line. (A queued entry the drain refuses shows the raw RAISE, which
  // names the bin and is already floor prose.)
  [/so no more weight can go into it/i,
   'That bin is no longer open — it was closed, sold, released, merged or removed after this screen loaded — so no more weight can go into it. Pick an open bin or start a new one, then save again.'],
  // Same guard, failing closed: the bin id names no bin this business can
  // write (another business's bin, e.g. a device-minted id that collided).
  [/not available in this business, so this entry cannot go into it/i,
   'That bin is not available in this business, so this entry can\'t go into it. Pick an open bin or start a new one, then save again.'],
  // create_reject_dispatch — the bin moved on between list-load and release.
  [/must be READY or AT_STALL to dispatch/i,
   'That bin is no longer Ready or At stall, so it can\'t be dispatched — someone else has already moved it. Refresh the bin and check its current status.'],
  [/Reject bin .* not found in this tenant/i,
   'That bin is no longer in this business — it may have been deleted or transferred. Refresh the list and try again.'],
  // mig 479 transfer_reject_bins — bin→cage merge.
  [/transfer_reject_bins: bin .* status is/i,
   'One of the source bins is no longer Ready or At stall, so it can\'t be merged into a cage. Refresh and re-pick the sources.'],
  [/transfer_reject_bins: bin .* has no remaining weight/i,
   'One of the source bins has nothing left in it. Refresh and re-pick the sources.'],
  // mig 20261152000000 (U06 R2a) — waste never mixes into a sellable cage.
  [/transfer_reject_bins: WASTE bins cannot be transferred together/i,
   'Waste bins can\'t be transferred together with reject bins — waste is declared unfit and stays apart. Transfer the waste bins on their own.'],
  [/transfer_reject_bins: source bins span multiple growers/i,
   'A cage must hold fruit from one grower only, and the bins you picked come from more than one. Transfer them as separate cages.'],
  [/transfer_reject_bins: source total .* destination total/i,
   'The cage weights don\'t add up to the source bins — net fruit weight has to be conserved. Check each cage\'s weight and try again.'],
  [/transfer_reject_bins: source bins not found/i,
   'Those source bins aren\'t available in this business any more. Refresh and re-pick.'],
  // record_dispatch_cages — cages are recorded against a signed-out load.
  [/record_dispatch_cages: cages can only be recorded once the load is DISPATCHED/i,
   'Cages can only be recorded once the load has been dispatched (driver signed). Complete the dispatch first, then record the cages.'],
  [/record_dispatch_cages: dispatch .* has no buyer/i,
   'This load has no buyer on it, so cages can\'t be booked out against one. Set the buyer on the dispatch first.'],
  [/record_dispatch_cages: cage_type .* not found/i,
   'One of the cage types on this sheet no longer exists — re-pick it from the list.'],
  // confirm_reject_dispatch_receipt.
  [/must be DISPATCHED \(driver signed\) before receipt can be recorded/i,
   'This load hasn\'t been dispatched yet, so a receipt can\'t be recorded against it. Capture the driver signature first.'],
  // void_reject_dispatch. ("Dispatch NNN is already voided" names the dispatch
  // number, not a UUID, so it is left to pass through as written.)
  [/void_reject_dispatch: dispatch .* not found/i,
   'That dispatch couldn\'t be found — it may already have been removed. Refresh and try again.'],
  // mig 20261152000000 (U07 R3b) — a note the buyer received cannot be voided.
  [/void_reject_dispatch: dispatch .* was received by the buyer/i,
   'The buyer has received this load, so the note can no longer be voided.'],
  // mig 20261066500000 — the reject_bins / reject_log guard triggers (F06 S1).
  // They refuse a DIRECT edit by anyone below admin; the screens' own buttons
  // never trip them, so an operator meets one only on a stale screen or a race.
  [/reject_bins_guard: only an admin can delete a reject bin/i,
   'Only an admin can delete a reject bin. If the fruit is gone, Dispose of the bin instead.'],
  [/reject_bins_guard: only an admin can change a bin from/i,
   'Only an admin can change a bin between Reject and Waste. Ask an admin to use Edit bin.'],
  [/reject_bins_guard: .* can only be set by the release, verify or transfer steps/i,
   'That part of the bin (its delivery note, truck, release price or admin check) is set by the release, verify or transfer steps, not by editing the bin. Refresh the bin and use its buttons.'],
  [/reject_bins_guard: only a manager can record a farmer collection/i,
   'Only a manager can hand a bin to the farmer. Ask a manager to record the collection.'],
  [/reject_bins_guard: only a manager or an admin can change the weight or stall takings/i,
   'Only a manager or an admin can change a bin\'s weight or its stall takings. Ask a manager to log the sale or correct the weight.'],
  [/reject_bins_guard: only a manager or an admin can close a stall bin as collected/i,
   'Only a manager or an admin can close a stall bin as sold out or collected. To write the fruit off, use Dispose.'],
  // mig 20261152000000 (U06 R2a) — waste is declared unfit; it never reaches the stall.
  [/reject_bins_guard: bin .* is WASTE — declared unfit, it cannot go to the stall/i,
   'That bin is marked WASTE — it was declared unfit, so it can\'t go to the stall or be sold. Dispose of it from the bin\'s own page instead.'],
  [/reject_bins_guard: a bin cannot be moved from .* to .* by editing it/i,
   'That bin can\'t move to that status from where it is now — someone may have moved it already. Refresh the bin and check its status.'],
  [/reject_log_guard: run .* is verified/i,
   'This run has been verified and published to the grower, so its rejects are locked. Ask an admin to make the change.'],
  // PART 5 (F06 BININS, Jason 5 Oct) — a direct INSERT lands only for a fresh,
  // empty bin on a run of this business. "+ Start a new bin" sends exactly that,
  // so the floor meets the run refusal only after switching business with Log
  // Rejects open; the other one only from a hand-made write.
  [/reject_bins_guard: a new bin must be started on a run of this business/i,
   'A new bin can only be started on a run of this business. If you switched business with this page open, refresh it, pick the run again and start the bin.'],
  [/reject_bins_guard: a new bin starts Open and empty/i,
   'A new bin starts Open and empty: its weight comes from the rejects logged into it, and its sale, release and checks from the bin\'s own buttons. Start it with "+ Start a new bin" on Log Rejects.'],
  // mig 20261051300000 (F06 D1) — a login whose token names no business (a
  // user removed with Revoke, a member of a suspended business). Raised as 42501
  // by every reject + truck RPC, so the row has to beat the generic RLS sentence.
  [/: you are not signed in to a business/i,
   'You\'re not signed in to a business any more — your access may have been removed. Sign out and back in, or ask your admin to restore your access.'],
  // mig 20261051300000 (F06 D1) — the new cages take the source bins' business,
  // so one transfer may not mix two. The screen lists one business's bins, so
  // only a direct call gets here; the row is kept because the F06 brief gives
  // every new RAISE an operator sentence, and it costs one line.
  [/transfer_reject_bins: source bins belong to more than one business/i,
   'The bins you picked belong to different businesses, so they can\'t go into one cage. Transfer each business\'s bins separately.'],
]

// ---------------------------------------------------------------------------
// Operator sentences for this screen's RPC backstops (migs 476 / 645 / 658 /
// 659). Every one of these RAISEs is prefixed with the function name and
// interpolates a raw UUID — "attach_bins_to_consignment: bin 4c1e-… is already
// on another consignment" — and friendlyError passes a P0001 through verbatim
// by design, so this registry is what stands between the floor and our
// function names. Rows are the states an operator can genuinely race into
// (two people loading the same truck); anything unmatched still falls through
// to the platform sentences rather than being swallowed.
// ---------------------------------------------------------------------------
export const REJECT_DISPATCH_DOMAIN: [RegExp, string][] = [
  // mig 645 — the WASTE guard. Money-relevant: waste must never be sold.
  [/is WASTE — declared unfit/i,
   'That bin is marked WASTE — it was declared unfit, so it can\'t be sold or consigned. Dispose of it from the bin\'s own page instead.'],
  [/bin .* is already on another consignment/i,
   'That bin is already on another consignment — remove it there first, or refresh to see where it went.'],
  [/attach_bins_to_consignment: bin .* status is/i,
   'That bin is no longer Ready or At stall, so it can\'t be loaded. Refresh the bin list and re-pick.'],
  [/attach_bins_to_consignment: bin .* belongs to a different tenant/i,
   'That bin belongs to another business, so it can\'t be loaded here.'],
  [/attach_bins_to_consignment: consignment .* not found/i,
   'This consignment couldn\'t be found — it may have been cancelled. Refresh the mission.'],
  [/attach_bins_to_consignment: consignment status is/i,
   'This consignment is no longer a draft, so bins can\'t be added to it. Refresh — it has probably already been dispatched.'],
  // mig 658 — detach.
  [/detach_bin_from_consignment: consignment status is/i,
   'This consignment is no longer a draft, so bins can\'t be removed from it. Refresh — it has probably already been dispatched.'],
  [/detach_bin_from_consignment: bin .* is not attached to consignment/i,
   'That bin isn\'t on this consignment any more — someone else has already removed it. Refresh the list.'],
  [/detach_bin_from_consignment: consignment .* not found/i,
   'This consignment couldn\'t be found — it may have been cancelled. Refresh the mission.'],
  // mig 476 — create / dispatch / receipt / void.
  [/create_reject_consignment: mission .* already holds pallets/i,
   'This trip is already carrying pallets, so reject consignments can\'t be added to it. Start a separate reject trip.'],
  [/create_reject_consignment: mission .* not found/i,
   'That trip couldn\'t be found — it may have been cancelled. Refresh and try again.'],
  [/create_reject_consignment: unknown route/i,
   'That destination isn\'t a route this packhouse recognises — pick one from the list.'],
  [/dispatch_reject_mission: no consignment has bins attached/i,
   'Nothing is loaded on this trip yet — add at least one bin to a consignment before dispatching.'],
  [/dispatch_reject_mission: mission status is/i,
   'This trip can\'t be dispatched from its current state — refresh; it may already have gone out or been cancelled.'],
  [/dispatch_reject_mission: mission kind is/i,
   'This is a pallet trip, so it has to be dispatched from the pallet dispatch screen.'],
  [/confirm_consignment_receipt: consignment status is/i,
   'A receipt can only be recorded once this consignment has been dispatched. Refresh — it may already have been received.'],
  [/confirm_consignment_receipt: received_by_name is required/i,
   'Enter the name of the person who received the load.'],
  [/void_reject_mission: reason is required/i,
   'A reason is required to void a trip.'],
  [/void_reject_mission: mission .* not found/i,
   'That trip couldn\'t be found — it may already have been voided. Refresh and try again.'],
  [/void_reject_mission: mission kind is/i,
   'This is a pallet trip — void it from the pallet dispatch screen instead.'],
  // mig 659 — cage returns.
  [/record_cage_returns: consignment .* has no buyer/i,
   'One of these consignments has no buyer on it, so cages can\'t be booked against one. Set the buyer first.'],
  [/record_cage_returns: consignment .* not found/i,
   'One of these consignments is no longer available — refresh the mission and try again.'],
  [/record_cage_returns: cage_type .* not found/i,
   'One of the cage types on this sheet no longer exists — re-pick it from the list.'],
  [/record_cage_returns: (no lines supplied|nothing to record)/i,
   'Nothing to record — enter a cage type and a quantity on at least one consignment.'],
  [/record_cage_returns: qty must be > 0/i,
   'Every cage line needs a quantity greater than zero.'],
  // mig 20261066500000 PART 4 — mission_cage_movements_ids_guard (F06 CAGEIDS).
  // Raised 42501, so without this row it reads as the bare permission line.
  // Edit cage movement's own lists hold only this business's buyers and cage
  // types, so an operator meets it only on a stale screen.
  [/cage_movement_guard: that .* belongs to another business/i,
   'That buyer, cage type, trip or delivery note belongs to another business, so this cage movement can\'t use it. Refresh and pick it again from the list.'],
  // mig 20261051300000 (F06 D3) — the truck + cage RPCs need Reject Dispatch →
  // Manage. The buttons are already hidden from roles without it, so this is
  // reached by a custom role that shows them and lacks the key, or a stale tab.
  [/: needs the Reject Dispatch manage permission/i,
   'Only a manager can do this on a reject trip — your role doesn\'t have Reject Dispatch "Manage". Ask a manager, or ask your admin to add it to your role.'],
  // mig 20261051300000 (F06 D3, ruled 2 Oct) — the buttons for a new
  // mission were removed in UF-11, but the server still refuses it; the
  // message stays for old tabs and API callers. "Confirm receipt at buyer"
  // needs Reject Dispatch → Edit.
  [/create_reject_consignment: needs the Reject Dispatch edit permission/i,
   'Your role can\'t start a reject trip — it needs Reject Dispatch "Edit". Ask a manager, or ask your admin to add Edit on Reject Dispatch to your role.'],
  // Counting cages out and in (record_cage_return) is Edit too (Jason, 14 Oct:
  // cage counts stay open to the floor).
  [/record_cage_return: needs the Reject Dispatch edit permission/i,
   'Your role can\'t record cages — it needs Reject Dispatch "Edit". Ask a manager, or ask your admin to add Edit on Reject Dispatch to your role.'],
  [/confirm_consignment_receipt: needs the Reject Dispatch edit permission/i,
   'Your role can\'t record a receipt at the buyer — it needs Reject Dispatch "Edit". Ask a manager, or ask your admin to add Edit on Reject Dispatch to your role.'],
  // mig 20261051300000 (F06 D3) — a trip that has left can't be voided.
  [/void_reject_mission: mission status is/i,
   'This trip has already been dispatched or voided, so it can\'t be voided. Refresh the trip.'],
  // mig 20261051300000 (F06 D1) — a login whose token names no business (a
  // user removed with Revoke, a member of a suspended business). Raised as 42501
  // by every reject + truck RPC, so the row has to beat the generic RLS sentence.
  [/: you are not signed in to a business/i,
   'You\'re not signed in to a business any more — your access may have been removed. Sign out and back in, or ask your admin to restore your access.'],
  // mig 20261051300000 (F06 D1 / S6) — record_cage_return now checks the
  // consignment or trip it is given. The Cages tab passes neither, so only a
  // direct call reaches these; they carry a raw UUID, and the F06 brief gives
  // every new RAISE an operator sentence.
  [/record_cage_return: (consignment|mission) .* belongs to a different business/i,
   'That trip belongs to another business, so cages can\'t be booked against it here. Refresh — you may have switched business in another tab.'],
  [/record_cage_return: (consignment|mission) .* not found/i,
   'That trip or consignment couldn\'t be found for these cages — it may have been cancelled. Refresh and try again.'],
  // mig 20261051300000 (ruled 2 Oct ~18:00) — the buyer and cage type on a cage
  // movement must be this business's own (a shared cage type passes). The
  // Cages-tab pickers read them under RLS, so a real tap reaches the first row
  // from a list loaded under another business (a business switch, or platform
  // mode, whose RLS reads every business's buyers and cage types). On Dispatch
  // truck a cage line's buyer is NOT picked: it is the consignment's own, so a
  // refusal there is platform mode's cage-type list, or a consignment that
  // already names another business's buyer (create_reject_consignment does
  // not check it) — refreshing cannot fix that one, so the sentence says how
  // to dispatch without that line.
  [/record_cage_return: (buyer|cage type) .* belongs to a different business/i,
   'That buyer or cage type belongs to another business, so these cages weren\'t booked. Refresh the page and pick them again — you may have switched business in another tab.'],
  [/dispatch_reject_mission: (buyer|cage type|consignment) .* belongs to a different business/i,
   'A buyer, cage type or consignment in the cages on this truck belongs to another business, so the truck was not dispatched. Pick each "Cage type going out" again (in platform mode the list holds every business\'s cage types). If it still fails, a consignment names another business\'s buyer: set its cage type to "— no cage capture —", dispatch, and ask your admin to correct that consignment.'],
]

// The stock RPCs' RAISE backstops that do NOT arrive as P0001 and so would
// reach the operator with a bare code prefix ("[22023] stock item not found"),
// plus the two void_stock_movement rows that carry the function name. The
// balance guard ("only 3 on hand at the source store — cannot transfer 5") and
// the already-voided guards are P0001 and already read as operator prose, so
// they are deliberately left to pass through as written.
export const STOCK_DOMAIN: [RegExp, string][] = [
  [/stock item not found/i,
   'That stock item no longer exists — refresh the list and try again.'],
  [/stocktake count cannot be negative/i,
   'A stocktake count can\'t be negative — enter zero or more.'],
  [/void_stock_movement: a reason is required/i,
   'A reason is required to void a movement.'],
  [/void_stock_movement: movement .* not found/i,
   'That movement couldn\'t be found — it may already have been voided. Refresh the list.'],
  // record_stock_movement's role gate (42501). Since mig 20261054700000 (F12
  // R1) Receive and Issue also pass for the stock "edit" right; Adjust and a
  // count from the card stay with the manager roles. Unmapped, friendlyError's
  // 42501 branch said "ask an admin to check your role", which names no right
  // an admin could grant.
  [/Only admins or managers can record stock movements/i,
   'Receiving and issuing stock needs the stock "edit" right in this business, and adjusting or counting an item needs a manager or admin. Ask an admin for the right, or a manager to record it.'],
]

// This session's queued count lines, oldest first — the order the drain
// applies them in.
// post_stock_count_session raises its state guards with ERRCODE 22023, NOT the
// P0001 that friendlyError passes through as operator prose — so without these
// rows a counter reads "[22023] count session is POSTED — only OPEN or
// SUBMITTED sessions can be posted". Same for the two lookup guards.
export const COUNT_SESSION_DOMAIN: [RegExp, string][] = [
  [/count session is .* only OPEN or SUBMITTED sessions can be posted/i,
   'This count has already been posted or cancelled, so it can\'t be posted again. Refresh to see its current state.'],
  [/count session not found/i,
   'That count session no longer exists — refresh the list.'],
  [/stock item not found/i,
   'One of the counted items no longer exists in the stock list — refresh and re-check the worksheet.'],
  [/stocktake count cannot be negative/i,
   'A counted quantity can\'t be negative — enter zero or more.'],
]

// The pallet-edit guards that raise with an ORDINARY PG code rather than
// P0001. A RAISE carrying ERRCODE 23514 is indistinguishable, to
// friendlyError, from a real CHECK constraint firing — so its §5.4 branch
// answers with the generic "a value doesn't pass one of this record's
// validation rules" and buries a sentence that was already written for the
// operator behind the Details disclosure. That is exactly what a kf49 admin
// photographed on 2026-09-10 trying to edit an externally received pallet.
//
// Screens hitting the guard through SubmitOverlay pass this as its `domain`.
export const PALLET_DOMAIN: [RegExp, string][] = [
  // pallets_block_external_product_code (mig 20260807090000 §4). product_id
  // is the key that auto-issues packaging stock, so a pallet that arrived
  // already packed must keep it NULL for life.
  [/Externally received pallets cannot be assigned a packaging product/i,
   'This pallet arrived already packed, so it can\'t carry one of our packaging codes — no cartons of ours were used on it. Edit its counts, weights and grade on the receipt it came in on instead.'],
  // enforce_pallet_receipt_tenant (same migration §3).
  [/pallet_receipt tenant .* does not match its (run|owner)/i,
   'This receipt doesn\'t belong to the business you\'re signed in to. Switch business and try again.'],
  // pallet_lines_tenant_matches_pallet (mig 20261044900000): ERRCODE 42501, so
  // without a row it reads as the generic "you don't have permission".
  [/A pallet line must belong to the same business as its pallet/i,
   'This pallet belongs to a different business than the one you\'re signed in to. Switch business and try again.'],
  [/That pallet does not exist, so a line cannot be added to it/i,
   'That pallet could not be found, so nothing was saved. Refresh the page and try again.'],
]

// Writing a tip needs the same permission as undoing one: has_perm('tipping',
// 'edit') (BF-04, mig 20261032960000). It reaches a screen in two shapes, and
// friendlyError's 42501 branch would answer both with the generic "you don't
// have permission for that action", naming nothing an admin can grant:
//   * tip_cooling_batch / retip_bin RAISE their own sentence (42501);
//   * a delivery tip is a plain insert, so the tipping_insert policy answers
//     with PostgREST's bare RLS text, which names the table.
// Both get the one sentence, naming the permission as the Roles editor shows
// it (module "Tipping", action "Edit"). The tip screens also hide their
// controls from a role without it (usePermissions), so this is what a device
// whose profile caps tipping meets — get_my_permissions does not apply the
// device ceiling, has_perm does.
export const TIP_NEEDS_PERMISSION =
  'Tipping needs the Tipping "Edit" permission, and your role doesn\'t have it, so nothing was tipped. Ask an admin to add it to your role.'

export const TIP_PERMISSION_DOMAIN: [RegExp, string][] = [
  // 42501 — tip_cooling_batch / retip_bin's own check.
  [/tipping into a run needs the Tipping "Edit" permission/i, TIP_NEEDS_PERMISSION],
  // 42501 — the tipping_insert policy refused a delivery tip.
  [/row-level security policy for table "tipping_log"/i, TIP_NEEDS_PERMISSION],
]

/**
 * The tip screens' sentence for a permission refusal, or null when `err` is
 * not one — so every other refusal (a closed run, a bin already tipped) keeps
 * the text it always showed.
 */
export function tipPermissionRefusal(err: unknown): string | null {
  const { message } = friendlyError(err, TIP_PERMISSION_DOMAIN)
  return message === TIP_NEEDS_PERMISSION ? message : null
}

// The deleted-delivery freeze (mig 20261033300000:
// aaa_deliveries_frozen_when_deleted_trg on deliveries and
// aaa_delivery_additions_frozen_when_parent_deleted_trg on its loads). Both
// RAISE the same sentence with ERRCODE 23514 — on purpose, so the offline queue
// strands a replay at once instead of retrying it five times — and a 23514 is
// what friendlyError's CHECK branch answers with "a value doesn't pass one of
// this record's validation rules", burying the reason behind Details.
//
// A screen meets it when it was opened before someone else deleted the
// delivery (the deleted view itself writes nothing). The offline badge shows
// the RAISE text as it is, which is already the operator sentence.
export const DELETED_DELIVERY_DOMAIN: [RegExp, string][] = [
  [/This delivery has been deleted, so it and its loads can.t be changed/i,
   'This delivery has been deleted, so it can\'t be changed. Refresh the page. If the change is still needed, an admin restores the delivery under Config → Admin → Recycle bin first.'],
]

// The lot guard on a build-up line (enforce_tip_lot_same_run, mig
// 20261019100000, fired by pallet_lines_lot_same_run). Since mig
// 20261025200000 the build-up dialog sends each line's lot, so the dialog now
// meets this guard. Its three RAISEs interpolate the raw tip UUID and carry
// ORDINARY PG codes, so neither mapping worked: the dialog's own error line
// printed "Lot 4c1e-… was tipped into a different run — a pallet_lines row can
// only name a tip on its own run", and friendlyError's code branches answered
// with a sentence about something else — 23503 "linked to other records",
// 23514 "doesn't pass one of this record's validation rules", 42501 "you
// don't have permission".
//
// Scoped to the build-up dialog: the same trigger guards pallets and
// reject_log, whose screens would want different words.
export const BUILD_UP_LOT_DOMAIN: [RegExp, string][] = [
  // 23503 — the tip was undone (undo_tipping DELETEs it, which also clears
  // every line that named it) while this dialog still held it.
  [/Lot .+ does not exist — the tip may have been undone/i,
   'A lot picked on this pallet no longer exists — its tip was undone after these lines were loaded. Check the Lot on each line, pick again (or “no lot”), and save.'],
  // 23514 — the lot is a tip on another run than the line's source run.
  [/Lot .+ was tipped into a different run/i,
   'One line names a lot from a different run. A line can only take a lot tipped into its own source run — check the Lot on each line (pick it again after changing a line’s source run) and save.'],
  // 42501 — the tip is another tenant's.
  [/Lot .+ belongs to another business/i,
   'One line names a lot that belongs to another business, so it can’t go on this pallet. Pick that line’s lot from its own source run and save.'],
]

/**
 * The build-up dialog's sentence for a lot-guard refusal, or null when `err` is
 * not one. Lets the dialog's error line re-word just these refusals and leave
 * every other failure reading as it did.
 */
export function buildUpLotRefusal(err: unknown): string | null {
  const { message } = friendlyError(err, BUILD_UP_LOT_DOMAIN)
  return BUILD_UP_LOT_DOMAIN.some(([, sentence]) => sentence === message) ? message : null
}

// save_pallet_build_up refuses a DISPATCHED pallet (mig 20261042500000, 22023),
// in the words of set_pallet_line_variety's refusal. Unmapped, 22023 reads as
// "something in the request isn't valid" and gives no next step. The screen
// hides the button on a dispatched pallet, so an operator only meets this when
// the pallet was dispatched in another tab or by someone else while the dialog
// was open.
export const BUILD_UP_DISPATCHED_DOMAIN: [RegExp, string][] = [
  [/This pallet is already dispatched; its build-up lines cannot be changed here/i,
   'This pallet has been dispatched, so its build-up lines are locked. Close this window. To correct the lines, an admin must undispatch the pallet first.'],
]

/** The build-up dialog's sentence for the dispatched-pallet refusal, or null. */
export function buildUpDispatchedRefusal(err: unknown): string | null {
  const { message } = friendlyError(err, BUILD_UP_DISPATCHED_DOMAIN)
  return BUILD_UP_DISPATCHED_DOMAIN.some(([, sentence]) => sentence === message) ? message : null
}

// Bins → Issue and Bins → Returns send a client key with every insert (mig
// 20261054300000, F08 D1). The key is minted once per entry and resent on a
// retry of that entry, so a 23505 on one of these two indexes means the FIRST
// try landed — the save timed out on floor wifi after the row was written. It
// is not a failure: the loan (or return) is in the ledger exactly once, which
// is what the operator wanted. The forms read it through binLedgerAlreadySaved()
// below and report it as a success; it is listed here so the sentence is pinned
// against the literal PostgREST text like every other domain row.
/** What a retry of a return that already landed reads (a 23505 on the key index,
 *  or `already_saved` from record_bin_return_split). */
export const BIN_RETURN_ALREADY_SAVED = 'Already saved — this return was recorded on the first try.'

export const BIN_LEDGER_DOMAIN: [RegExp, string][] = [
  [/unique constraint "bin_loans_client_key_uidx"/i,
   'Already saved — this loan was recorded on the first try.'],
  [/unique constraint "bin_returns_client_key_uidx"/i,
   BIN_RETURN_ALREADY_SAVED],
]

// record_bin_return_split (mig 20261209500000, UF-16). Its RAISEs are 22023 /
// 42501, which friendlyError prints as "[22023] …" or as "ask an admin to check
// your role". The over-return sentence carries counts the registry cannot
// interpolate, so this row is the BACKSTOP: the form checks the number it has on
// screen first and says the exact figure; this wording reads when another device
// moved the loans in between. Pinned to the migration text by the sibling test.
export const BIN_RETURN_SPLIT_DOMAIN: [RegExp, string][] = [
  [/but only \d+ bins of this type (?:are|were) out for this grower/i,
   'This grower has fewer bins of this type out than you entered, so nothing was saved. Check the count, or open "Against one loan…" to record more on one loan.'],
  [/No bins of this type (?:are|were) out for this grower/i,
   'No bins of this type are out for this grower, so nothing was saved. Check the bin type, or open "Against one loan…".'],
  [/The loans changed while this return was saving/i,
   'Another change moved these loans while the return was saving, so nothing was saved. Tap Receive return again.'],
  [/A return can not be dated in the future/i,
   'A return can not be dated in the future. Pick today or an earlier day.'],
  [/Permission denied: your role in this business cannot record a bin return/i,
   'Your role in this business cannot record a bin return. Ask an admin.'],
  [/Support view is read-only\. Leave support view to record a return/i,
   'Support view is read-only. Leave support view to record a return.'],
]

// cancel_stored_bin (mig 20261209500000, UF-19). The reason and state refusals
// are 22023, which would print as "[22023] …"; the permission one is 42501.
export const BIN_CANCEL_DOMAIN: [RegExp, string][] = [
  [/Bin .+ is already cancelled/i,
   'This bin is already cancelled.'],
  [/Bin .+ has already been tipped into a run/i,
   'This bin has already been tipped into a run, so it can not be cancelled.'],
  [/Bin .+ is on \d+ run\(s\), so it can not be cancelled/i,
   'This bin is on a run, so it can not be cancelled. Remove it from the run first.'],
  [/Bin .+ was deleted, so it can not be cancelled/i,
   'This bin was deleted, so it can not be cancelled.'],
  [/Bin .+ came off a received pallet/i,
   'This bin came off a received pallet, so it can not be cancelled. Use Put back on pallet instead.'],
  [/Bin not found\./i,
   'This bin was not found. Refresh the page.'],
  [/Give a reason for cancelling this bin/i,
   'Give a reason of at least 3 characters.'],
  [/Permission denied: cancelling a stored bin needs the Bin Inventory/i,
   'Cancelling a stored bin needs the Bin Inventory Manage permission. Ask an admin.'],
  [/Support view is read-only\. Leave support view to cancel a bin/i,
   'Support view is read-only. Leave support view to cancel a bin.'],
]

/**
 * The "already saved" sentence when `err` is the client-key refusal of a bin
 * loan / return insert (the retry of a save that landed), or null for every
 * other failure — those still fail.
 */
export function binLedgerAlreadySaved(err: unknown): string | null {
  const { message } = friendlyError(err, BIN_LEDGER_DOMAIN)
  return BIN_LEDGER_DOMAIN.some(([, sentence]) => sentence === message) ? message : null
}

// The Tipping page's one-tap stored-bin re-tip (retip_bin; body last re-emitted
// by mig 20261032960000, RAISE texts as in 20260929154233). Two of its refusals carry ERRCODE 23514, which friendlyError
// cannot tell from a real CHECK constraint — so without these rows a scale
// operator whose run was stopped on another device read "a value doesn't pass
// one of this record's validation rules", the run-state word only behind
// Details. The bin guard is P0001 and passes through verbatim, raw bin UUID and
// all — and it, not the OPTLK version check, is what the loser of two stations
// racing for one bin meets (FOR UPDATE waits, then reads the bin as TIPPED).
//
// Only the states an operator can race into from the board are listed; every
// other RAISE in the body still falls through to the platform sentences.
//
// The delivery tip's closed-run guard (calculate_tipping_net_weight, mig
// 20261028100000) is 23514 too, so on this screen it read as the generic
// validation line. Its RAISE is already a sentence and the OFFLINE badge shows
// it as written (markFailed stores the raw message); this row is for the live
// tip, where friendlyError would otherwise bury it behind Details.
export const TIPPING_DOMAIN: [RegExp, string][] = [
  // 23514 — a delivery tip onto a run that left ACTIVE after the board loaded
  // (Tipping stopped / Complete / Verified / Archived).
  [/Run .+ is closed \(.+\), so this load was not tipped/i,
   'This run has stopped taking tips, so this load was not tipped and the delivery still has its bins. Go back and tip it onto a run that is tipping, or ask an admin to reopen this run.'],
  // 23514 — the run left ACTIVE (Stop tipping / Complete) after the board loaded.
  [/a bin can only be re-tipped into an ACTIVE run/i,
   'This run has stopped taking tips, so the bin was not tipped — it is still in stock. Tip it onto a run that is tipping, or ask an admin to reopen this run.'],
  // 23514 — the run was deleted after the board loaded.
  [/Run .+ is deleted — pick an active run/i,
   'This run was deleted, so it has stopped taking tips — the bin was not tipped and is still in stock. Go back and pick a run that is tipping.'],
  // 23514 — the delivery's bins were tipped elsewhere first (the over-tip guard;
  // migs 809 and 20261028100000 word it "already tipped + now exceeds the …
  // received", mig 20261019150000 "on other tips + here exceeds").
  [/Over-tip: .+ exceeds the .+ received/i,
   'Not tipped: another device has already tipped some of these bins, so this load is more than the bins left on the delivery. Check Bins remaining, then tip the smaller number.'],
  // 23514 — gross weight at or below the bins' tare.
  [/Tipping net is .+ kg \(gross .+ less tare\)/i,
   'Not tipped: the net weight comes to zero or less. Check the gross weight and the bin quantity, then tip again.'],
  // P0001 — the bin left stock (another station tipped it first).
  [/Bin .+ is .+ — only a stored bin can be re-tipped/i,
   'This bin isn\'t in stock any more — most likely it was just tipped on another device — so it was not tipped again.'],
  // 23514 — linked runs J4 (mig 20261288000000): retip_bin's own sentences,
  // written for the floor, shown word for word (they name the bin and the run).
  [/^(Bin .+ is a bulk-sort pallet\. It goes onto a packing-line run, not back onto a bulk sort\.)$/, '$1'],
  [/^(Bin .+ is .+'s fruit\. Run .+ is .+'s\. Pick another bin or another run\.)$/, '$1'],
  [/^(Bin .+ is .+\. Run .+ (?:takes|packs) .+\. Pick another bin or another run\.)$/, '$1'],
]

/**
 * The Tipping page's sentence for a refusal TIPPING_DOMAIN maps, or null when
 * `err` is not one. The delivery tip's red line uses it so it says what the
 * overlay says, and every other failure keeps the text it always showed.
 */
export function tippingRefusal(err: unknown): string | null {
  const { message } = friendlyError(err, TIPPING_DOMAIN)
  return TIPPING_DOMAIN.some(([, sentence]) => sentence === message) ? message : null
}

// A load and its delivery belong to one business (mig 20261035400000). Two
// refusals, both ERRCODE 42501, which friendlyError answers with "you don't
// have permission … ask an admin to check your role" — the wrong next step:
// nobody's role is wrong, the screen is on a delivery of a business the
// session is no longer signed in to.
//
//   * aa_delivery_additions_same_tenant_as_delivery_trg — a load added to, or
//     moved onto, another business's delivery (Record Delivery's grouping, a
//     WhatsApp confirm or merge, the bins editor's adjustment load).
//   * admin_toggle_addition_checked's tenant check — and the same sentence
//     from its siblings admin_toggle_addition_tipping_check and
//     admin_delete_delivery_addition (P0001 there), which printed the load's
//     raw UUID.
//
//   * aa_deliveries_block_same_business_trg and the same load guard (mig
//     20261037300000) — a delivery or a load saved on a block of another
//     business (Record Delivery's lines, a WhatsApp confirm, a correction).
//
// An operator only meets these by switching business in another tab while a
// delivery screen stays open, or as a platform admin working another
// business's delivery while signed in to their own.
export const DELIVERY_LOAD_TENANT_DOMAIN: [RegExp, string][] = [
  [/A load must belong to the same business as its delivery/i,
   'This delivery isn\'t in the business you\'re signed in to now, so a load can\'t be added to it or moved onto it. Refresh the page — if you switched business in another tab, switch back and try again.'],
  [/Addition .+ belongs to another tenant/i,
   'This load isn\'t in the business you\'re signed in to now, so it can\'t be changed from here. Refresh the page — if you switched business in another tab, switch back and try again.'],
  [/A block must belong to the same business as its delivery/i,
   // The guard compares the block with the DELIVERY's business, not the signed-in
   // one: a platform admin signed in to B correcting A's delivery is refused for
   // picking B's block (security review, 5 Oct), so name the delivery's business.
   'That block isn\'t one of this delivery\'s business\'s blocks, so it can\'t go on this delivery or load. Refresh the page and pick one of the delivery\'s own blocks — if you switched business in another tab, switch back and try again.'],
]

// Every server refusal a delivery screen's writes can meet: the deleted-delivery
// freeze first, then the load tenant guards. One registry per screen, so the
// delivery page, its load rows and Record Delivery all answer both.
export const DELIVERY_SCREEN_DOMAIN: [RegExp, string][] = [
  ...DELETED_DELIVERY_DOMAIN,
  ...DELIVERY_LOAD_TENANT_DOMAIN,
]

// The pallet edit guard (mig 20261048100000) and the received-pallet RPCs (mig
// 20261048200000) refuse with ERRCODE 42501 and a sentence that already names
// the permission needed. friendlyError maps EVERY 42501 to its generic
// "you don't have permission" line, so without these rows the operator never
// reads which permission to ask for. Each sentence is passed through as
// written; the sibling test pins every pattern against the literal migration
// text.
const PALLET_EDIT_GUARD_DOMAIN: [RegExp, string][] = [
  [/^Editing a pallet needs the Pallets manage permission\./,
   'Editing a pallet needs the Pallets manage permission. Ask a manager. Nothing was changed.'],
  [/^Changing the SSC barcode needs the Pallets override permission\./,
   'Changing the SSC barcode needs the Pallets override permission. Ask an administrator. Nothing was changed.'],
  [/^Changing the pack date needs the back-date permission or the Pallets override permission\./,
   'Changing the pack date needs the back-date permission or the Pallets override permission. Ask an administrator. Nothing was changed.'],
  [/^You cannot back-date a new pallet\./,
   'You cannot back-date a new pallet. Back-dating the pack date needs the back-date permission or the Pallets override permission. Nothing was changed.'],
  [/^Editing a received pallet needs the Manager or Scale operator role/,
   'Editing a received pallet needs the Manager or Scale operator role, or the Deliveries edit permission. Ask an administrator. Nothing was changed.'],
  [/^Receiving pallets needs the Manager or Scale operator role\./,
   'Receiving pallets needs the Manager or Scale operator role. Ask an administrator. Nothing was changed.'],
  [/^An older received date needs the back-date permission or the Pallets override permission\./,
   'An older received date needs the back-date permission or the Pallets override permission. Ask a manager to enter it. Nothing was changed.'],
  [/^The received date cannot be in the future\./,
   'The received date cannot be in the future. Nothing was changed.'],
  [/^Changing a receipt's received date needs the back-date permission/,
   "Changing a receipt's received date needs the back-date permission or the Pallets override permission. Nothing was changed."],
  [/^An older pack date needs the back-date permission or the Pallets override permission\./,
   'An older pack date needs the back-date permission or the Pallets override permission. Ask a manager to enter it. Nothing was changed.'],
]

/** The guard's own sentence, or null for any other failure. */
export function palletEditGuardRefusal(err: unknown): string | null {
  const { message } = friendlyError(err, PALLET_EDIT_GUARD_DOMAIN)
  return PALLET_EDIT_GUARD_DOMAIN.some(([, sentence]) => sentence === message) ? message : null
}

// void_received_pallet's own gate (mig 20261304000000, UF-19). Both refusals are 42501, which
// friendlyError would answer with the generic "check your role" line; these name the right to ask
// for. The sibling test pins each pattern against the literal migration text.
export const RECEIVE_VOID_DOMAIN: [RegExp, string][] = [
  [/^You need the Pallets right Manage or Delete to void a received pallet/,
   'Voiding a received pallet needs the Pallets Manage or Delete right. Ask a manager or an admin. Nothing was changed.'],
  [/^Support view is read-only\. Leave support view to void a pallet\./,
   'Support view is read-only. Leave support view to void a pallet. Nothing was changed.'],
]

// return_pallet_to_inspection (mig 20261325000000, UF-18): the undo for Approve and for a house
// Reject. Every RAISE is 42501 / 22023 / P0002 / 55000 / OPTLK / 28000, which friendlyError would
// answer with a generic line (or the raw text with the pallet number in the middle of it). Each row
// says what stops the return and what to do. Pinned against the migration's literal RAISE text in
// packhouseErrorDomains.test.ts.
export const PALLET_RETURN_DOMAIN: [RegExp, string][] = [
  [/^Not authenticated$/,
   'Your session has ended. Sign in again. Nothing was changed.'],
  [/^Support view is read-only\. Nothing was changed\./,
   'Support view is read-only. Leave support view to return a pallet. Nothing was changed.'],
  [/^Returning a pallet to inspection needs the Inspection Manage right/,
   'Returning a pallet to inspection needs the Inspection Manage right. Ask a manager or an admin. Nothing was changed.'],
  [/^Say why in 3 to 500 characters/,
   'Say why in 3 to 500 characters. Nothing was changed.'],
  [/^Pallet .+ was not found\./,
   'This pallet was not found. Refresh the page. Nothing was changed.'],
  [/^Pallet .+ was just modified\./,
   'Someone changed this pallet just now. Refresh the page and try again. Nothing was changed.'],
  [/^Pallet .+ is dispatched\./,
   'This pallet is dispatched. Undispatch it first. Nothing was changed.'],
  [/^Pallet .+ is .+\. Only an approved or a rejected pallet can go back to inspection/,
   'Only an approved or a rejected pallet can go back to inspection. Nothing was changed.'],
  [/^Pallet .+ is on a truck\./,
   'This pallet is on a truck. Take it off the truck first. Nothing was changed.'],
  [/^Pallet .+ is on a truck consignment\./,
   'This pallet is on a truck consignment. Take it off the consignment first. Nothing was changed.'],
  [/^Pallet .+ is on a PPECB consignment\./,
   'This pallet is on a PPECB consignment. The PPECB verdict stands, so it cannot go back to inspection. Nothing was changed.'],
  [/^Pallet .+ was rejected by PPECB\./,
   'PPECB rejected this pallet. Only PPECB can clear that. Nothing was changed.'],
  [/^Pallet .+ is reserved on a trade deal\./,
   'This pallet is reserved on a trade deal. Release the reservation first. Nothing was changed.'],
  [/^Pallet .+ was already sent to Titan\./,
   'This pallet was already sent to Titan. It cannot go back to inspection. Nothing was changed.'],
]

// put_bin_back_on_pallet (mig 20261057100000): the undo for Retip cartons. Its
// refusals are 23514 / 42501 / P0002 / 22023, which friendlyError answers with
// "doesn't pass one of this record's validation rules" or "you don't have
// permission" — true, but no use to a manager standing at the bin. Each row says
// why this bin can't go back and what to do instead. Pinned against the
// migration's literal RAISE text in packhouseErrorDomains.test.ts.
export const PUT_BACK_DOMAIN: [RegExp, string][] = [
  [/Only managers can put cartons back on a pallet/i,
   'Only a manager can put cartons back on a pallet (the Pallets "Manage" permission). Ask a manager to do it.'],
  [/Say why, in a few words/i,
   'Say why, in a few words — e.g. "stripped the wrong pallet".'],
  [/^Bin not found/i,
   'This bin is gone — it may already have been put back. Refresh the page.'],
  [/^Bin .+ belongs to another business/i,
   'This bin belongs to another business. Refresh the page — if you switched business in another tab, switch back and try again.'],
  [/did not come off a received pallet/i,
   'This bin didn\'t come off a received pallet, so there\'s nothing to put it back on.'],
  [/is tipped into a run — Undo tip on that run first/i,
   'This bin is tipped into a run. Undo the tip on that run first, then put it back.'],
  [/only a bin on the stock sheet or in a coldroom can go back/i,
   'Only a bin on the stock sheet or in a coldroom can go back on its pallet.'],
  [/does not record how many cartons came off its pallet/i,
   'This bin doesn\'t record how many cartons came off its pallet, so they can\'t be put back automatically. Ask an admin.'],
  [/^Pallet .+ no longer exists/i,
   'The pallet this bin came off no longer exists, so there\'s nothing to put it back on.'],
  [/^Pallet .+ belongs to another business/i,
   'The pallet this bin came off belongs to another business, so its cartons can\'t go back on it from here.'],
  [/was not stripped from pallet .+ receipt/i,
   'This bin wasn\'t stripped from that pallet\'s receipt, so it can\'t go back on it.'],
  [/was deleted \(.*\) — it cannot take cartons back/i,
   'The pallet this bin came off was deleted, so it can\'t take cartons back.'],
  [/left on a truck — its cartons cannot go back/i,
   'The pallet this bin came off has left on a truck, so its cartons can\'t go back on it.'],
  [/cartons can only go back on a pallet that is staged or in a coldroom/i,
   'Cartons can only go back on a pallet that is staged or in a coldroom.'],
  [/has no record of bin .+ coming off it/i,
   'There\'s no record of this bin coming off its pallet, so its cartons can\'t be put back automatically. Ask an admin.'],
  [/was undispatched after bin .+ came off it/i,
   'This bin\'s pallet was undispatched after the bin came off it, so the pallet already shows these cartons. Putting them back too would count them twice. Ask an admin to delete the bin instead.'],
  [/^Pallet .+ has .+ lines — its cartons cannot be put back/i,
   'This bin\'s pallet has more than one line, so the cartons can\'t be put back automatically. Ask an admin.'],
  [/kg gross would be — correct the bin/i,
   'This bin holds more kg than its pallet\'s gross weight would be. Correct the bin\'s kg first, then put it back.'],
]

// The QC-FAIL hold (F03 UF-05) — fruit from a run whose critical QC check
// FAILED and nobody has released it. Two gates, two sentences written for us:
//   * pallets_block_approve_on_qc_fail (mig 20260703122000) refuses Approve at
//     inspection with 23514, "Pallet … draws from a run that FAILED a critical
//     QC check and has not been released — a manager must disposition
//     (release) the QC run before approval". friendlyError's CHECK branch
//     would bury it under "a value doesn't pass one of this record's
//     validation rules"; raw, it says "disposition".
//   * submit-ppecb-consignment answers 409 "Cannot submit to PPECB — fruit
//     draws from N run(s) with an unresolved QC FAIL. A manager must release
//     (disposition) the QC run before export submission." It arrives as a
//     ValidationError, whose text friendlyError returns as written — so the
//     screens ask qcHoldRefusal below, which reads the TEXT.
// The dispatch gate's "Mission % includes fruit from run % …" is Dispatch's
// own wording and is not listed here.
export const QC_HOLD_APPROVE =
  'A QC FAIL on this pallet\'s run is blocking it — a QC manager must release the run first.'
export const QC_HOLD_PPECB =
  'A QC FAIL on a run behind these pallets is blocking this submission — a QC manager must release the run first.'

export const QC_HOLD_DOMAIN: [RegExp, string][] = [
  [/draws from a run that FAILED a critical QC check and has not been released/i, QC_HOLD_APPROVE],
  [/Cannot submit to PPECB .* with an unresolved QC FAIL/i, QC_HOLD_PPECB],
]

/**
 * The inspection screens' sentence for a QC-FAIL hold, or null when `err` is
 * not one — so every other refusal (a stale write, PPECB's field errors) keeps
 * the text it always showed. Asks friendlyError about the message alone: the
 * PPECB refusal is a ValidationError, and friendlyError returns those as
 * written without consulting a domain.
 */
export function qcHoldRefusal(err: unknown): string | null {
  const message = (err as { message?: unknown } | null)?.message
  if (typeof message !== 'string') return null
  const friendly = friendlyError(message, QC_HOLD_DOMAIN).message
  return QC_HOLD_DOMAIN.some(([, sentence]) => sentence === friendly) ? friendly : null
}

// allocate_carrier_invoice (mig 20260812180000, re-emitted 20261002083015)
// raises its two list guards with ERRCODE 22023, which friendlyError does not
// pass through as prose: re-pointing a bill's carrier and then pressing Allocate
// printed "Load <uuid> is not a live leg of this invoice's carrier in this
// tenant". Its amount/total guards are plain sentences and stay as written.
export const RECON_DOMAIN: [RegExp, string][] = [
  [/is not a live leg of this invoice'?s carrier/i,
   'One of the ticked loads does not belong to this invoice\'s carrier any more, or was deleted. Close this window, open Allocate again and tick the loads again.'],
  [/the same load appears twice in the allocation/i,
   'A load is ticked twice. Close this window, open Allocate again and tick the loads again.'],
]

// A placement onto a deleted run (mig 20261065000000, pallets_placement_guard,
// #2995 residual 8a). RAISEd as 42501 with the pallet and run numbers in it,
// which friendlyError answers with "you don't have permission … ask an admin to
// check your role" — wrong: no role can place there, the run is deleted. A
// screen meets it when the run was deleted after the page loaded.
export const PLACE_ON_DELETED_RUN_DOMAIN: [RegExp, string][] = [
  [/^Pallet .+ cannot be placed in a coldroom while its run .+ is deleted/,
   'This pallet\'s run was deleted, so it can\'t be placed in a coldroom. Restore the run first, or move the pallet onto a live run.'],
]

/** The sentence for that refusal, or null when `err` is not one. */
export function placeOnDeletedRunRefusal(err: unknown): string | null {
  const { message } = friendlyError(err, PLACE_ON_DELETED_RUN_DOMAIN)
  return PLACE_ON_DELETED_RUN_DOMAIN.some(([, sentence]) => sentence === message) ? message : null
}

// correct_run_variety (mig 20261090000000, ui-flow F21 UF-07: Change fruit). Its
// RAISEs are already operator sentences, but they reach the screen as 22023 (no
// branch in friendlyError, so the text gains a "[22023]" prefix), 42501 (the
// generic "ask an admin to check your role", which hides WHY: a run with fruit
// on it has nothing to do with a role) and P0002. The refusals that name the run
// (RUN-2026-0412 already has fruit tipped...) read "This run ..." here: the
// operator is looking at that run, and a registry sentence is static. The sibling
// test reads every RAISE from the migration, so a reworded one fails there.
export const RUN_FRUIT_DOMAIN: [RegExp, string][] = [
  [/^Only managers or admins can change a run'?s fruit/i,
   'Only managers or admins can change a run\'s fruit.'],
  [/^Pick the variety this run is for/i,
   'Pick the variety this run is for.'],
  [/^Give a reason for the change/i,
   'Give a reason for the change (a few words).'],
  [/^Run not found\. It may have been deleted/i,
   'Run not found. It may have been deleted.'],
  [/^This run belongs to another business/i,
   'This run belongs to another business. Switch business and try again.'],
  [/^A run of received pallets keeps the sender'?s fruit/i,
   'A run of received pallets keeps the sender\'s fruit.'],
  [/ is not active\. Only an active, empty run can change its fruit/i,
   'This run is not active. Only an active, empty run can change its fruit.'],
  [/ already has fruit tipped\. Correct the delivery instead/i,
   'This run already has fruit tipped. Correct the delivery instead, or undo the tips first.'],
  [/ was started from a delivery\. Correct the delivery instead/i,
   'This run was started from a delivery. Correct the delivery instead.'],
  [/ holds stored bins\. Take them off the run first/i,
   'This run holds stored bins. Take them off the run first.'],
  [/ already has pallets\. They carry the run'?s fruit/i,
   'This run already has pallets. They carry the run\'s fruit and cannot be re-labelled.'],
  [/ already has bins or rejects recorded from its fruit/i,
   'This run already has bins or rejects recorded from its fruit.'],
  [/ already has QC records on its fruit/i,
   'This run already has QC records on its fruit.'],
  [/ already has packing recorded on it/i,
   'This run already has packing recorded on it.'],
  [/^\S+ is already on .+\.$/i,
   'This run is already on that variety. Pick another variety.'],
  [/^PPECB variety code ".*" is not in the PPECB list/i,
   'That PPECB variety code is not in the PPECB list.'],
  [/^Variety ".*" does not match a fruit\. Add it under Commodities first/i,
   'That variety does not match a fruit. Add it under Commodities first.'],
  [/^The run was not changed\. Your role may not edit runs/i,
   'The run was not changed. Your role may not edit runs.'],
]

// Cooling moves (Precool board: Cancel, Start cool, Hold, Release, bulk move).
// apply_cooling_transition and the cooling_batches trigger (mig 20260730070407)
// raise sentences that carry the function or table name and a raw batch UUID,
// and the 24 h capture limit is 23U03, which friendlyError has no sentence for.
// Only the states an operator can race into are listed; anything else falls
// through to the platform sentences.
export const COOLING_DOMAIN: [RegExp, string][] = [
  // P0002 — the batch was removed or never reached this business.
  [/apply_cooling_transition: batch .+ not found/i,
   'That batch can no longer be found. Refresh the board and try again.'],
  // The batch changed state on another device (or it is a tipped / cancelled batch).
  [/cooling_batches: illegal transition .+ -> .+ \(batch tips go through Tipping\)/i,
   'That batch cannot make this move from its current stage. Another device may have moved it. Refresh the board to see where it is now. A batch is tipped from Tipping.'],
  [/only QC or management may release a batch to production/i,
   'Only QC or management can release a batch to production. Ask a QC user or a manager to release it.'],
  // 23U03 — the stored capture time is older than the 24 h window.
  [/apply_cooling_transition: captured time .+ is more than 24 h old/i,
   'This move was captured more than 24 hours ago, so it cannot be saved here. Ask a manager to set the true times with the back-date correction.'],
  [/apply_cooling_transition: captured time .+ is in the future/i,
   'The time on this move is in the future. Check the clock on this device and try again.'],
]

// Cold treatment (USA) lots (mig 20261129500000). A pallet joins a lot only in the lot's first hour
// (cold_treatment_lot_pallets_check, 23514); friendlyError's 23514 branch would answer the generic
// "a value doesn't pass one of this record's validation rules". The RAISE is already an operator
// sentence, so the row restates it as the screen's sentence and keeps the raw text under Details.
export const COLD_TREATMENT_DOMAIN: [RegExp, string][] = [
  [/This lot started more than an hour ago\. Start a new lot for this pallet/i,
   'This lot started more than an hour ago. Start a new lot for this pallet.'],
]

// Delivery bin re-allocation (mig 20261259000000, M6 D2): admin_correct_delivery (grower change),
// admin_set_delivery_uses_packhouse_bins, admin_rerun_delivery_bin_allocation and the
// deliveries_bin_identity_guard_trg trigger. The state refusals are 22023 (which would print as
// "[22023] …"); the permission, support-view and guard ones are 42501. Each row restates the RAISE
// as the screen's sentence; the raw text stays under Details.
export const DELIVERY_BIN_DOMAIN: [RegExp, string][] = [
  // $1 = the old grower, $2 = the bins that have nowhere to go.
  [/^(.+) returned (\d+) bin\(s\) against the bins issued on this delivery/i,
   '$1 returned $2 bin(s) against the bins issued on this delivery, and has no other open loan to move them to. Record a loan for $1 first, or leave the grower as it is.'],
  [/These bins are on issued invoice .+\. Void it first, then correct the delivery\./i,
   'These bins are on an issued invoice. Void that invoice first, then correct the delivery.'],
  [/grower-bins record that is already marked returned/i,
   'This delivery has a grower-bins record that is already marked returned. Fix that record in Bin Inventory → Grower bins first, then try again.'],
  [/change only through its correction screen, so the bin ledger follows/i,
   'The grower and the packhouse-bins tick of a received delivery change only through Correct delivery, so the bin ledger follows. Open the delivery and use Correct delivery.'],
  [/Support view is read-only\. Leave support view to (correct a delivery|change a delivery|re-run bin allocation)/i,
   'Support view is read-only. Leave support view to change this delivery.'],
  [/Only managers or admins can (change whether a delivery used packhouse bins|re-run bin allocation)/i,
   'Only managers or admins can change a delivery’s bins. Ask a manager.'],
  [/Give a reason for (the change|re-running bin allocation) \(a few words\)/i,
   'Give a reason (a few words).'],
  [/Say whether the delivery used packhouse bins/i,
   'Say whether the delivery used packhouse bins.'],
  [/This delivery belongs to another business/i,
   'This delivery belongs to another business.'],
]

// hold_qc_run (mig 20261326000000, M4b UF-19) and the support-view refusal it added to
// disposition_qc_run. The permission and support-view refusals are 42501, the state refusals
// 22023 / 55000, the miss P0002: friendlyError would answer with its generic "check your role",
// "validation rules" or raw-code lines. Each row restates the RAISE as the screen's sentence.
// Pinned against the migration's literal text in packhouseErrorDomains.test.ts.
export const QC_HOLD_DECISION_DOMAIN: [RegExp, string][] = [
  [/^Not authenticated/,
   'Your sign-in has ended. Sign in again, then try again.'],
  [/^Support view is read-only\. Nothing was changed\./,
   'Support view is read-only. Nothing was changed.'],
  [/^Only managers or admins can hold a failed QC run/,
   'Only a QC manager can hold a failed QC run. Ask a manager. Nothing was changed.'],
  [/^Give a reason for holding this QC run/,
   'Give a reason for the hold (3 to 2000 characters).'],
  [/^QC run not found\./,
   'This QC run is gone, or it belongs to another business. Refresh the page.'],
  [/^This QC run was voided\./,
   'This QC run was voided. A voided run cannot be held. Refresh the page.'],
  [/^This QC run is .+ and not finished\./,
   'This QC run is not finished yet. Only a finished failed run can be held.'],
  [/^This QC run is .+ and not a fail\./,
   'This QC run did not fail. Only a failed run can be held.'],
  [/^This QC run is already released\./,
   'This QC run is already released. A hold changes nothing.'],
]

// recall_investigations drill CHECKs (mig 20261346000000, R-A8). A drill start the table refuses
// (older than 7 days, or after the save) leaves the manager unable to save. The fix is on the
// timer, so the sentence says so. Pinned against the migration's constraint names in
// packhouseErrorDomains.test.ts.
export const MOCK_RECALL_DRILL_DOMAIN: [RegExp, string][] = [
  [/recall_investigations_drill_within_7d|recall_investigations_drill_start_not_future|recall_investigations_drill_start_needs_flag/,
   'Cancel the mock recall timer, then save.'],
]

// Records on hold + 5-year retention (migs 20261351000000 / 20261352000000). The guards raise
// RHOLD (on hold) and RRETN (kept for retention) with a sentence written for the operator; these
// rows only tidy it for the screens that show it (Labour settings, the upload void). PRDLK is the
// older closed-pay-period refusal, whose raw text names the table: void_labour_upload meets it
// when a day of the upload sits in an approved or paid run.
export const RETENTION_DOMAIN: [RegExp, string][] = [
  // records_retention_refuse(true, …)
  [/^(.+?) is on hold, so it cannot be deleted\./,
    '$1 is on hold. A labour manager must release the hold first. See Labour settings, Records on hold.'],
  // records_retention_refuse_edit(…): a raw edit that would drop the upload / adjustment mark of a source row
  [/^(.+?) is on hold, so it cannot be changed this way\./,
    '$1 is on hold. Do not edit it by hand. Use Correct punches on the day. See Labour settings, Records on hold.'],
  [/^(.+?) must be kept for (\d+) years, so it cannot be changed this way\./,
    '$1 must be kept for $2 years. Do not edit it by hand. Use Correct punches on the day.'],
  // labour_log_source_client_edit_guard: a raw client edit of an uploaded / hand-adjusted day
  [/^(.+?) cannot be changed this way\./,
    '$1 cannot be changed by hand. Use Correct punches on the day.'],
  // records_retention_refuse(false, …)
  [/^(.+?) must be kept for (\d+) years, so it cannot be deleted\./,
    '$1 must be kept for $2 years. It cannot be deleted.'],
  // payroll_lines_retention_guard / payroll_periods_retention_guard
  [/^This pay (?:line belongs to a pay run that is|run is) (\w+), so it cannot be deleted\./,
    'The pay run is $1. Re-open it to Draft before you delete anything on it.'],
  // labour_workers_retention_guard
  [/^This worker has records that must be kept for (\d+) years, so the worker cannot be deleted\./,
    'This worker has records that must be kept for $1 years. Set an end date instead of deleting the worker.'],
  // labour_log_block_locked_period (mig 552 family), met by void_labour_upload
  [/Cannot DELETE labour_log row: pay period is/i,
    'A day in this upload is in a closed pay run. Re-open the pay run first. Then void the upload.'],
]

// Farm-link consent locks (mig 20261348000000). The master guard on blocks / pucs and the
// grower_id lock on grower_tenant_links raise 42501, which friendlyError answers with its generic
// "you don't have permission" line. The sentences already say what to do, so each row passes the
// RAISE through as written. The merge refusal ("<name> is linked to a farm. ...") is P0001 and
// reaches the screen verbatim without a row. Pinned against the migration text in
// packhouseErrorDomains.test.ts.
export const GROWER_LINK_DOMAIN: [RegExp, string][] = [
  [/^That farm block is not shared with this grower\./,
   'That farm block is not shared with this grower. Link the grower to its farm first, and pick a block of that farm.'],
  [/^That farm PUC is not shared with this grower\./,
   'That farm PUC is not shared with this grower. Link the grower to its farm first, and pick a PUC of that farm.'],
  [/^grower_tenant_links\.grower_id cannot be changed/,
   'A farm link cannot be moved to another grower. Ask BigAgri support to end the link.'],
]
